import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { submitDetails } from '@/hooks/user';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Zod form schema for validation
const formSchema = z.object({
  channel: z
    .string({
      required_error: 'Please select a channel.',
    })
    .min(1, { message: 'Please select a channel' }),
  username: z
    .string({
      required_error: 'Username/Email cannot be empty.',
    })
    .trim()
    .min(1, { message: 'Username/Email cannot be empty' }),
  password: z
    .string({
      required_error: 'Password cannot be empty',
    })
    .min(1, { message: 'Password cannot be empty' }),
});

export default function DetailsSubmit() {
  const navigate = useNavigate();
  const channels = ['instagram', 'linkedin'];
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      channel: '',
      username: '',
      password: '',
    },
  });

  const onSubmit = async (values) => {
    await toast.promise(submitDetails(values), {
      loading: 'Submitting details...',
      success: (response) => {
        form.reset();
        return 'Details submitted successfully!';
      },
      error: (error) => {
        return (
          error.response?.data?.message ||
          'An error occurred while submitting the details'
        );
      },
    });
  };

  const handleReset = () => {
    form.reset();
    toast.info('Form has been reset');
  };

  const handleBack = () => {
    navigate('/inbox');
  };

  return (
    <div className='container relative flex h-screen items-center'>
      {/* Back Button */}
      <Button
        variant='outline'
        onClick={handleBack}
        className='absolute left-4 top-4'
      >
        ← Back to Inbox
      </Button>

      <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[450px]'>
        <div className='flex flex-col space-y-2 text-center'>
          <h1 className='text-2xl font-semibold tracking-tight'>
            Details Submit
          </h1>
          <p className='text-sm text-muted-foreground'>
            Enter your details below
          </p>
        </div>

        <div className='rounded-lg border bg-card px-6 py-8 shadow-sm'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              {/* Channel Selection */}
              <FormField
                control={form.control}
                name='channel'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Channel</FormLabel>
                    <FormControl>
                      <div className='grid grid-cols-2 gap-2'>
                        {channels.map((channel) => (
                          <div key={channel} className='relative'>
                            <input
                              type='radio'
                              id={channel}
                              value={channel}
                              checked={field.value === channel}
                              onChange={(e) => field.onChange(e.target.value)}
                              className='peer sr-only'
                            />
                            <Label
                              htmlFor={channel}
                              className='flex cursor-pointer items-center justify-center rounded-md border-2 border-muted bg-background px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-checked:shadow-sm'
                            >
                              <span className='capitalize'>{channel}</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='username'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username/Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Enter username or email'
                        type='text'
                        autoCorrect='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Enter password'
                        type='password'
                        autoCorrect='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex gap-4'>
                <Button
                  type='submit'
                  disabled={form.formState.isSubmitting}
                  className='flex-1'
                >
                  {form.formState.isSubmitting && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Save
                </Button>

                <Button
                  type='button'
                  variant='outline'
                  onClick={handleReset}
                  disabled={form.formState.isSubmitting}
                  className='flex-1'
                >
                  Reset
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
